import { ENGINE_LABELS, type Engine } from "@velivo/shared";

/**
 * FR-DASH-03 — Multi-Engine Narrative Health Breakdown.
 *
 * A hand-drawn SVG radar rather than a charting library: the whole chart is
 * four axes and five polygons, and pulling in a charting dependency for that
 * would add more surface than it saves (ARCHITECTURE.md's "check the component
 * table before introducing a new dependency").
 *
 * All four axes run 0-100 with higher being better, which is what makes the
 * shape readable at all — a radar mixing a "lower is better" axis in produces a
 * polygon whose area means nothing.
 */

export interface EngineAxis {
  engine: Engine;
  samples: number;
  accuracy: number | null;
  mptr: number | null;
  sentimentAlignment: number | null;
  citationAuthority: number | null;
  ndi: number | null;
}

export interface EngineBreakdown {
  axes: EngineAxis[];
  insight: string | null;
}

const AXES = [
  { key: "accuracy", label: "Accuracy" },
  { key: "mptr", label: "Pull-through" },
  { key: "sentimentAlignment", label: "Framing" },
  { key: "citationAuthority", label: "Source authority" },
] as const;

/** Distinguishable without relying on hue alone — each series also gets a dash
 *  pattern, so the chart survives both greyscale printing and colour blindness. */
const SERIES = [
  { stroke: "#4338CA", dash: "" },
  { stroke: "#0E7490", dash: "6 3" },
  { stroke: "#B45309", dash: "2 3" },
  { stroke: "#7E22CE", dash: "8 3 2 3" },
  { stroke: "#15803D", dash: "4 4" },
];

const SIZE = 280;
const CENTER = SIZE / 2;
const RADIUS = 96;

export function EngineRadar({ data }: { data: EngineBreakdown | null }) {
  if (!data) {
    return <div className="skeleton" style={{ height: 300 }} />;
  }

  const measured = data.axes.filter((a) => a.samples > 0);

  if (measured.length === 0) {
    return (
      <p className="note-empty">
        No engine has a scored response in this period yet. Run a capture on an
        approved prompt to populate this comparison.
      </p>
    );
  }

  const point = (axisIndex: number, value: number) => {
    // Start at 12 o'clock and go clockwise, which is how people read a radar.
    const angle = (Math.PI * 2 * axisIndex) / AXES.length - Math.PI / 2;
    const r = (Math.max(0, Math.min(100, value)) / 100) * RADIUS;
    return [
      CENTER + r * Math.cos(angle),
      CENTER + r * Math.sin(angle),
    ] as const;
  };

  return (
    <div className="radar">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="radar__chart"
        role="img"
        aria-label={`Engine comparison across ${AXES.map((a) => a.label).join(", ")}`}
      >
        {[25, 50, 75, 100].map((ring) => (
          <polygon
            key={ring}
            points={AXES.map((_, i) => point(i, ring).join(",")).join(" ")}
            fill="none"
            stroke="#E4E6EB"
          />
        ))}
        {AXES.map((axis, i) => {
          const [x, y] = point(i, 100);
          const [lx, ly] = point(i, 128);
          return (
            <g key={axis.key}>
              <line x1={CENTER} y1={CENTER} x2={x} y2={y} stroke="#E4E6EB" />
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10"
                fill="#5B6072"
              >
                {axis.label}
              </text>
            </g>
          );
        })}

        {measured.map((engine, seriesIndex) => {
          const series = SERIES[seriesIndex % SERIES.length]!;
          // An axis the judge never ran on is null. Drawing it at zero would
          // show a collapsed polygon that reads as catastrophic performance on
          // a dimension that was simply not measured — so those points sit at
          // the ring's midpoint and the legend says which are partial.
          const values = AXES.map((axis) => engine[axis.key] ?? 50);
          return (
            <polygon
              key={engine.engine}
              points={values.map((v, i) => point(i, v).join(",")).join(" ")}
              fill={series.stroke}
              fillOpacity={0.08}
              stroke={series.stroke}
              strokeWidth={1.75}
              strokeDasharray={series.dash}
            />
          );
        })}
      </svg>

      <div className="radar__side">
        {data.insight ? <p className="insight">{data.insight}</p> : null}
        <ul className="radar__legend">
          {measured.map((engine, i) => {
            const series = SERIES[i % SERIES.length]!;
            const partial = AXES.some((a) => engine[a.key] === null);
            return (
              <li key={engine.engine}>
                <span
                  className="radar__swatch"
                  style={{
                    borderColor: series.stroke,
                    borderStyle: series.dash ? "dashed" : "solid",
                  }}
                  aria-hidden="true"
                />
                <span>
                  <strong>
                    {ENGINE_LABELS[engine.engine] ?? engine.engine}
                  </strong>
                  <br />
                  <span className="muted">
                    NDI {engine.ndi ?? "—"} · {engine.samples}{" "}
                    {engine.samples === 1 ? "response" : "responses"}
                    {partial ? " · some axes not measured" : ""}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
